import type { MutableState } from '../State';
import type { SyncActionAPI } from '../utils/types';
import { duration } from '../utils/duration';
import { getVisitsCount } from './visit.getters';
import { gemsReward, visitListLimit } from './visit.ruleset';
import { endVisitAction } from './visit.util';

export function normalizeGemGrants(state: MutableState, now: number): void {
  const { gemGrants } = state.visit;

  for (const [id, timestamp] of Object.entries(gemGrants)) {
    // We need 1 day for functionality, 2 days including leeway
    if (now - timestamp > duration({ days: 2 })) {
      delete gemGrants[id];
    }
  }
}

/**
 * Cleanup all visited list that is not in recent visited list
 */
export function normalizeRecentVisitsFrom(state: MutableState): void {
  // [id, timestamp]
  const visitsArr = Object.entries(state.visit.recentVisitsFrom);

  if (visitsArr.length <= visitListLimit) {
    return;
  }

  visitsArr.sort((a, b) => b[1] - a[1]);

  visitsArr.length = visitListLimit;

  state.visit.recentVisitsFrom = {};

  for (let [id, timestamp] of visitsArr) {
    state.visit.recentVisitsFrom[id] = timestamp;
  }
}

/**
 * Cleanup all visited list that is not in recent visited list
 */
export function normalizeRecentVisitsTo(state: MutableState): void {
  // [id, timestamp]
  const visitsArr = Object.entries(state.visit.recentVisitsTo);

  if (visitsArr.length <= visitListLimit) {
    return;
  }

  visitsArr.sort((a, b) => b[1] - a[1]);

  visitsArr.length = visitListLimit;

  state.visit.recentVisitsTo = {};

  for (let [id, timestamp] of visitsArr) {
    state.visit.recentVisitsTo[id] = timestamp;
  }
}

/**
 * Increment count of times player has visited a friend
 * @param state Player state
 * @param friendId the friend being visited by the player
 * @param now the current time
 * @return the new visit count
 */
export function incrementVisitsCount(
  state: MutableState,
  friendId: string,
  now: number,
): number {
  const { visitsTo } = state.visit.counts;

  // Check if friend is already in recent visitors that we start counting from 1 not from 0
  const inRecentVisits = friendId in state.visit.recentVisitsTo;
  const initValue = inRecentVisits ? 1 : 0;
  const currentCount = visitsTo[friendId] ?? initValue;

  state.visit.lastVisitAt = now;
  state.visit.recentVisitsTo[friendId] = now;

  // Skip visits from friends that are already in recent visitors
  if (currentCount >= 1) {
    visitsTo[friendId] = currentCount + 1;
  }

  normalizeRecentVisitsTo(state);

  normalizeVisitsTo(state);

  return getVisitsCount(state, friendId);
}

/**
 * Increment count of times player has been visited by a friend
 * @param params.friendId the friend visiting the player
 * @param params.name display name of the visiting friend
 * @param params.now the current time
 * @return the new visited count
 */
export function incrementVisitedCount(
  state: MutableState,
  params: {
    friendId: string;
    name: string;
    now: number;
  },
): number {
  const { friendId, name, now } = params;
  const { visitsFrom } = state.visit.counts;

  // Check if friend is already in recent visitors that we start counting from 1 not from 0
  const inRecentVisitors = friendId in state.visit.recentVisitsFrom;
  const initValue = inRecentVisitors ? 1 : 0;
  const currentCount = visitsFrom[friendId] ?? initValue;

  state.visit.lastVisitorName = name;

  // Skip visits from friends that are already in recent visitors
  if (currentCount >= 1) {
    visitsFrom[friendId] = currentCount + 1;
  }

  state.visit.recentVisitsFrom[friendId] = now;

  normalizeRecentVisitsFrom(state);

  // Cleanup all visitors that is not in recent visitors list
  normalizeVisitsFrom(state);

  return visitsFrom[friendId] ?? initValue;
}

/**
 * Increment amount of gems received by player
 * @param state
 * @param friendId the friend visiting the player
 * @param now the current time
 */
export function incrementGemsGrants(
  state: MutableState,
  friendId: string,
  now: number,
): void {
  const { gemGrants, visitSession } = state.visit;

  visitSession!.gemsReceived += gemsReward;
  state.visit.gemsGrantsToday++;
  state.visit.lastGemsGrantAt = now;
  gemGrants[friendId] = now;

  normalizeGemGrants(state, now);
}

/**
 * Cleanup all visited list that is not in recent visited list
 */
export function normalizeVisitsTo(state: MutableState): void {
  const { visitsTo } = state.visit.counts;

  for (const friendId of Object.keys(visitsTo)) {
    // Remove all visited items that is not in recent visited list
    if (!state.visit.recentVisitsTo[friendId]) {
      delete visitsTo[friendId];
    }
  }
}

/**
 * Cleanup all visitors that is not in recent visitors list
 */
export function normalizeVisitsFrom(state: MutableState): void {
  const { visitsFrom } = state.visit.counts;

  for (const friendId of Object.keys(visitsFrom)) {
    // Remove all visitors that is not in recent visitors list
    if (!state.visit.recentVisitsFrom[friendId]) {
      delete visitsFrom[friendId];
    }
  }
}

/**
 * Cleans up the current visit session when a user logs in.
 *
 * If a visit session exists in the state, this function ends the visit session
 * by invoking `endVisitAction`, which deletes the session from the state.
 *
 * @param state - The mutable application state containing visit information.
 * @param api - The API object for performing synchronous actions.
 */
export function cleanupVisitSession(
  state: MutableState,
  api: SyncActionAPI,
): void {
  if (state.visit.visitSession) {
    endVisitAction(() => delete state.visit.visitSession, state, api, true);
  }
}
